import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BackgroundJobService from '../../services/backgroundjobs/BackgroundJobService';
import {
  clearBackgroundJob,
  setActiveBackgroundJob,
  setBackgroundJobPolling,
  setBackgroundJobs,
} from '../../store/slices/backgroundjobs/backgroundJobSlice';
import { AppDispatch, RootState } from '../../store/store';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';

const JOBS_POLLING_INTERVAL = 3000;

export const useBackgroundJob = () => {
  const dispatch: AppDispatch = useDispatch();

  const { handle } = useApiErrorHandler();
  const { show } = useAlerts();

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const handleRef = useRef(handle);
  const showRef = useRef(show);

  const [downloadingJobUuid, setDownloadingJobUuid] = useState<
    string | null
  >(null);

  const [downloadProgress, setDownloadProgress] = useState<
    number | null
  >(null);

  const { activeJob, jobs, isPolling } = useSelector(
    (state: RootState) => state.backgroundJob
  );

  useEffect(() => {
    handleRef.current = handle;
  }, [handle]);

  useEffect(() => {
    showRef.current = show;
  }, [show]);

  const stopJobsPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);

      intervalRef.current = null;
    }

    dispatch(setBackgroundJobPolling(false));
  }, [dispatch]);

  const refreshActiveJobs = useCallback(async () => {
    try {
      const response =
        await BackgroundJobService.getActiveJobs();

      const activeJobs = Array.isArray(response)
        ? response
        : [];

      dispatch(setBackgroundJobs(activeJobs));

      return activeJobs;
    } catch (error: any) {
      handleRef.current(error);

      return [];
    }
  }, [dispatch]);

  const startJobsPolling = useCallback(() => {
    if (intervalRef.current) {
      return;
    }

    dispatch(setBackgroundJobPolling(true));

    const poll = async () => {
      try {
        const response =
          await BackgroundJobService.getActiveJobs();

        const currentJobs = Array.isArray(response)
          ? response
          : [];

        dispatch(setBackgroundJobs(currentJobs));

        const hasRunningJobs = currentJobs.some(
          (job) =>
            job.status === 'PENDING' ||
            job.status === 'PROCESSING'
        );

        if (!hasRunningJobs) {
          stopJobsPolling();
        }
      } catch (error: any) {
        stopJobsPolling();

        handleRef.current(error);
      }
    };

    intervalRef.current = setInterval(() => {
      void poll();
    }, JOBS_POLLING_INTERVAL);

    void poll();
  }, [dispatch, stopJobsPolling]);

  const pollJob = useCallback(
    (uuid: string) => {
      const interval = setInterval(async () => {
        try {
          const job =
            await BackgroundJobService.getProgress(uuid);

          dispatch(setActiveBackgroundJob(job));

          if (job.status === 'COMPLETED') {
            clearInterval(interval);

            showRef.current(
              'success',
              'Descarga lista',
              'El archivo ya está preparado.'
            );

            await refreshActiveJobs();

            return;
          }

          if (
            job.status === 'FAILED' ||
            job.status === 'CANCELED' ||
            job.status === 'EXPIRED'
          ) {
            clearInterval(interval);

            showRef.current(
              'error',
              'Error',
              job.errorMessage ||
                'No se pudo generar la descarga.'
            );

            await refreshActiveJobs();
          }
        } catch (error: any) {
          clearInterval(interval);

          handleRef.current(error);
        }
      }, JOBS_POLLING_INTERVAL);
    },
    [dispatch, refreshActiveJobs]
  );

  const downloadJobFile = useCallback(
    async (uuid: string, fileName?: string) => {
      if (downloadingJobUuid !== null) {
        return;
      }

      setDownloadingJobUuid(uuid);
      setDownloadProgress(0);

      try {
        const downloadedFileName =
          await BackgroundJobService.download(
            uuid,
            fileName || 'descarga.zip',
            (progress) => {
              setDownloadProgress(progress);
            }
          );

        showRef.current(
          'success',
          'Descarga completada',
          `Se descargó ${downloadedFileName}.`
        );

        dispatch(clearBackgroundJob());

        await refreshActiveJobs();
      } catch (error: any) {
        handleRef.current(error);
      } finally {
        setDownloadingJobUuid(null);
        setDownloadProgress(null);
      }
    },
    [
      dispatch,
      downloadingJobUuid,
      refreshActiveJobs,
    ]
  );

  return {
    activeJob,
    jobs,
    activeJobs: jobs,
    isPolling,

    downloadingJobUuid,
    downloadProgress,

    refreshActiveJobs,
    startJobsPolling,
    stopJobsPolling,

    pollJob,
    downloadJobFile,

    clearJob: () => dispatch(clearBackgroundJob()),
  };
};